import { useEffect, useState, type PropsWithChildren } from "react";
import Pagination from "@react/widget/Pagination";
import Icon from "@react/widget/Icon";
import Provider from "@module/apollo/client/Provider";
import useGetCareersPostsQuery from "@graphql/careersPosts/GetCareersPosts.CSR";

interface Props extends PropsWithChildren {
  siteHandle: string;
  category?: string | string[];
  relatedToEntries?: EntryCriteriaInput | EntryCriteriaInput[];
  relatedToSection?: string;
  title?: string;
  ids?: string[];
  page?: number;
  label: Record<string, any>;
}

const GetCareersPosts: React.FC<Props> = ({
  siteHandle,
  category,
  relatedToEntries,
  relatedToSection,
  title,
  ids,
  page,
  label
}) => {
  return (
    <Provider>
      <GetCareersPostsContent
        siteHandle={siteHandle}
        category={category}
        relatedToEntries={relatedToEntries}
        relatedToSection={relatedToSection}
        title={title}
        ids={ids}
        page={page}
        label={label}
      />
    </Provider>
  );
};

const GetCareersPostsContent: React.FC<Props> = ({
  siteHandle,
  category,
  relatedToEntries,
  relatedToSection,
  title,
  ids,
  page: _page = 1,
  label,
}) => {
  const [page, setPage] = useState(_page);
  const limit = 9;
  const offset = (page - 1) * limit;

  const { loading, error, data, refetch } = useGetCareersPostsQuery({
    site: siteHandle,
    topic: category,
    id: ids,
    relatedToEntries,
    limit,
    offset
  });

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    if (search.has("refresh")) refetch();
  }, []);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;

  if (data) {
    const count = data.entryCount;
    const totalPages = Math.ceil(count / limit);
    if(!totalPages) return <p>No posts found.</p>;
    return (
      <>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center [&>div]:max-w-sm [&>div]:w-full">
          {data.entries?.map((entry) => {
            const { title, url, blogFeaturedImage } = entry;
            return (
              <div
                key={title}
                className="flex bg-white flex-col text-left rounded-lg p-0 relative [&>img]:aspect-[16/9] items-stretch max-w-md transition duration-500 hover:shadow-lg [&:hover_img]:scale-110"
              >
                <a
                  className="bg-gray-50 rounded-t-lg [&>img]:h-[218px] [&>img]:w-[384px] min-h-[218px] overflow-hidden"
                  href={url}
                >
                  <img
                    className="rounded-t-lg w-full h-full object-cover transition duration-500"
                    src={blogFeaturedImage?.[0]?.src || "https://assets.avepoint.com/upload/blog/featured-image/default-feature-image.png"}
                    alt={title}
                  />
                </a>
                <div className="flex flex-col justify-between h-full p-5 border-r border-b border-l rounded-b-lg relative min-h-[200px]">
                  <h3 className="text-base md:text-xl">
                    <a href={url} className="hover:text-primary transition duration-1000 line-clamp-3">{title}</a>
                  </h3>
                  <div className="mt-4">
                    <a
                      className="btn flex font-semibold transition duration-300 cursor-pointer [&>*]:align-middle bg-white text hover:text-primary-hover active:bg-white [&_.icon]:text-primary group/btn w-full sm:w-fit py-2 md:py-[13px] text-base rounded-full px-0 justify-start !pb-0"
                      href={url}
                    >
                      <p className="h-full flex items-center justify-center">
                        <span className="btn-text">Read the Blog</span>
                        <Icon className="ml-5 transition-all duration-300 group-hover/btn:translate-x-2" name="arrow-right-long" />
                      </p>
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <Pagination
          label={label}
          page={page}
          total={totalPages}
          onChange={setPage}
        />
      </>
    );
  }
};

export default GetCareersPosts;